import { useEffect, useRef, useState } from 'react';
import ThreadPerfCard from './ThreadPerfCard';
import PlayerDropCard from './PlayerDropCard';
import FullPerfCard from './FullPerfCard';
import { useSetDashboardData } from './dashboardHooks';
import { getSocket } from '@/lib/utils';
import ServerStatsCard from './ServerStatsCard';
import QuickConsoleCard from './QuickConsoleCard';
import ActivityTimelineCard from './ActivityTimelineCard';
import ResourcePerformanceCard from './ResourcePerformanceCard';
import { useAtomValue } from 'jotai';
import { txConfigStateAtom, globalStatusAtom } from '@/hooks/status';
import { useLocation } from 'wouter';
import { TxConfigState, FxMonitorHealth } from '@shared/enums';
import ModalCentralMessage from '@/components/ModalCentralMessage';
import GenericSpinner from '@/components/GenericSpinner';
import { useBackendApi } from '@/hooks/fetch';
import { Button } from '@/components/ui/button';
import { CpuIcon, Loader2Icon, PlayIcon, RefreshCwIcon, SquareIcon } from 'lucide-react';
import { cn } from '@/lib/utils';


function DashboardPageInner() {
    const pageSocket = useRef<ReturnType<typeof getSocket> | null>(null);
    const setDashboardData = useSetDashboardData();
    const globalStatus = useAtomValue(globalStatusAtom);
    const [actionLoading, setActionLoading] = useState<string | null>(null);

    const triggerAction = useBackendApi({
        method: 'POST',
        path: '/server/action',
    });

    const handleAction = async (action: string) => {
        setActionLoading(action);
        try {
            await triggerAction({
                data: { action },
                toastLoadingMessage: `Sending ${action} command...`,
                genericHandler: {
                    successMsg: `Server ${action} request completed successfully.`
                }
            });
        } catch (error) {
            console.error(error);
        } finally {
            setActionLoading(null);
        }
    };

    // Run on mount only
    useEffect(() => {
        pageSocket.current = getSocket(['dashboard']);
        pageSocket.current.on('connect', () => {
            console.log("Dashboard Socket.IO Connected.");
        });
        pageSocket.current.on('disconnect', (message) => {
            console.log("Dashboard Socket.IO Disconnected:", message);
        });
        pageSocket.current.on('error', (error) => {
            console.log('Dashboard Socket.IO error', error);
        });
        pageSocket.current.on('dashboard', function (data) {
            setDashboardData(data);
        });

        return () => {
            pageSocket.current?.removeAllListeners();
            pageSocket.current?.disconnect();
        }
    }, []);

    const isOnline = globalStatus?.server?.health === FxMonitorHealth.ONLINE || globalStatus?.server?.health === FxMonitorHealth.PARTIAL;
    const serverName = globalStatus?.server?.name ?? 'SC Labs FXServer';
    const serverUptime = Math.floor((globalStatus?.server?.uptime ?? 0) / 1000);

    const formatUptime = (seconds: number) => {
        if (!seconds) return 'Offline';
        const hrs = Math.floor(seconds / 3600);
        const mins = Math.floor((seconds % 3600) / 60);
        if (hrs > 0) {
            return `${hrs}h ${mins}m`;
        }
        return `${mins}m`;
    };

    return (
        <div className="w-full min-w-96 flex flex-col gap-6">
            {/* Header Server Info Banner */}
            <div className="w-full bg-[#0d101d]/40 border border-white/5 backdrop-blur-xl rounded-xl p-5 flex flex-col md:flex-row items-center justify-between gap-4 shadow-lg relative overflow-hidden group">
                <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-scCyan/10 to-transparent" />
                
                {/* Server Status Details */}
                <div className="flex items-center gap-4">
                    <div className={cn(
                        "w-12 h-12 rounded-lg flex items-center justify-center border transition-colors duration-300",
                        isOnline ? "bg-scGreen/10 border-scGreen/25 text-scGreen" : "bg-destructive/10 border-destructive/25 text-destructive"
                    )}>
                        <CpuIcon className={cn("size-6", isOnline && "animate-pulse")} />
                    </div>
                    <div className="flex flex-col">
                        <div className="flex items-center gap-2">
                            <h2 className="text-lg font-bold text-white tracking-tight">{serverName}</h2>
                            <span className={cn(
                                "text-[10px] font-bold px-2 py-0.5 rounded-full border transition-all duration-300",
                                isOnline 
                                    ? "bg-scGreen/5 border-scGreen/15 text-scGreen shadow-[0_0_8px_rgba(0,255,102,0.05)]" 
                                    : "bg-destructive/5 border-destructive/15 text-destructive"
                            )}>
                                {isOnline ? 'RUNNING' : 'OFFLINE'}
                            </span>
                        </div>
                        <span className="text-xs text-muted-foreground font-mono mt-1">
                            Uptime: <strong className="text-slate-200">{formatUptime(serverUptime)}</strong>
                        </span>
                    </div>
                </div>

                {/* Server Controls Action Buttons */}
                <div className="flex items-center gap-3 w-full md:w-auto">
                    {/* Start Button */}
                    <Button
                        onClick={() => handleAction('start')}
                        disabled={isOnline || actionLoading !== null}
                        className={cn(
                            "flex-1 md:flex-none h-9 text-xs px-4 bg-scGreen/10 text-scGreen border border-scGreen/20 hover:bg-scGreen/20 focus:ring-0",
                            isOnline && "opacity-40 cursor-not-allowed"
                        )}
                    >
                        {actionLoading === 'start' ? (
                            <Loader2Icon className="animate-spin size-3.5 mr-2" />
                        ) : (
                            <PlayIcon size={14} className="mr-2 fill-current" />
                        )}
                        Start
                    </Button>

                    {/* Restart Button */}
                    <Button
                        onClick={() => handleAction('restart')}
                        disabled={actionLoading !== null}
                        className="flex-1 md:flex-none h-9 text-xs px-4 bg-scCyan/10 text-scCyan border border-scCyan/20 hover:bg-scCyan/20 focus:ring-0"
                    >
                        {actionLoading === 'restart' ? (
                            <Loader2Icon className="animate-spin size-3.5 mr-2" />
                        ) : (
                            <RefreshCwIcon size={14} className="mr-2" />
                        )}
                        Restart
                    </Button>

                    {/* Stop Button */}
                    <Button
                        onClick={() => handleAction('stop')}
                        disabled={!isOnline || actionLoading !== null}
                        className={cn(
                            "flex-1 md:flex-none h-9 text-xs px-4 bg-destructive/10 text-destructive border border-destructive/20 hover:bg-destructive/20 focus:ring-0",
                            !isOnline && "opacity-40 cursor-not-allowed"
                        )}
                    >
                        {actionLoading === 'stop' ? (
                            <Loader2Icon className="animate-spin size-3.5 mr-2" />
                        ) : (
                            <SquareIcon size={14} className="mr-2 fill-current" />
                        )}
                        Stop
                    </Button>
                </div>
            </div>

            {/* High Density 2-Column Responsive Dashboard Layout */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 w-full items-start">
                
                {/* Left 2 Columns: Telemetry / Charts */}
                <div className="xl:col-span-2 flex flex-col gap-6 w-full">
                    {/* KPI Cards Row */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 w-full">
                        <ServerStatsCard />
                    </div>

                    {/* Diagnostics & Hitches Row */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
                        <ThreadPerfCard />
                        <PlayerDropCard />
                    </div>

                    {/* Full History Performance Area Chart */}
                    <FullPerfCard />
                </div>

                {/* Right 1 Column: Operations Stack (Log, Script List, Terminal Console) */}
                <div className="xl:col-span-1 flex flex-col gap-6 w-full">
                    <ActivityTimelineCard />
                    <ResourcePerformanceCard />
                    <QuickConsoleCard />
                </div>

            </div>
        </div>
    );
}


export default function DashboardPage() {
    const txConfigState = useAtomValue(txConfigStateAtom);
    const setLocation = useLocation()[1];

    if (txConfigState === TxConfigState.Setup) {
        setLocation('/server/setup');
        return null;
    } else if (txConfigState === TxConfigState.Deployer) {
        setLocation('/server/deployer');
        return null;
    } else if (txConfigState !== TxConfigState.Ready) {
        return <div className='size-full'>
            <ModalCentralMessage>
                <GenericSpinner msg={`Unknown Config State: ${String(txConfigState)}`} />
            </ModalCentralMessage>
        </div>;
    } else {
        return <DashboardPageInner />;
    }
}
